import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isMandatory } from 'utils/simple-validations';
import {
  loadOfferDecisionSelectedConditions,
  loadOfferDecisionAvailableConditions,
  updateOfferDecisionSelectedConditions
} from 'actions/actionCreators/offer/offerDecisionActions';
import { withTranslation } from 'react-i18next';

import AssignmentLate from '@mui/icons-material/AssignmentLate';
import { CollapseCard } from '@totalsoft/rocket-ui';
import WrappedFakeText from 'components/common/WrappedFakeText';
import memoizeOne from 'memoize-one';
import * as editableDocument from 'utils/model/editableDocument';
import { collection } from 'utils/immutable';
import flow from 'lodash.flow';
import { when, valueChanged } from 'utils/fn-combinators';
import memoize from 'lodash.memoize';

//sub-components
import OfferDecisionConditionListComponent from './OfferDecisionConditionListComponent';

class OfferDecisionConditionContainer extends PureComponent {
  state = {
    loading: false,
    saving: false,
    prevPropsSelectedConditions: null,
    selectedConditions: null
  };

  static getDerivedStateFromProps(props, state) {
    if (props.selectedConditions !== state.prevPropsSelectedConditions) {
      return {
        prevPropsSelectedConditions: props.selectedConditions,
        selectedConditions: props.selectedConditions.map(editableDocument.create)
      };
    }
    return null;
  }

  static getItemValidationRules = () => ({
    decisionConditionId: [isMandatory()]
  });

  static getItemBusinessRules = ({ availableConditions }) => ({
    composedCode: when(
      valueChanged(condition => condition.decisionConditionId),
      condition => availableConditions.find(x => x.decisionConditionId === condition.decisionConditionId).composedCode
    )
  });

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.documentId !== this.props.documentId && prevProps.siteId !== this.props.siteId) {
      this.loadData();
    }
  }

  loadData = async () => {
    this.setState({ loading: true });
    try {
      await Promise.all([
        this.props.actions.loadOfferDecisionSelectedConditions(this.props.documentId, this.props.siteId),
        this.props.actions.loadOfferDecisionAvailableConditions()
      ]);
    } catch (error) {
      // continue regardless of error
    }
    this.setState({ loading: false });
  };

  getRemainingConditions = memoizeOne((availableConditions, ...selectedIds) => {
    return availableConditions.filter(value => selectedIds.every(x => x !== value.decisionConditionId));
  });

  changeItemProperty = memoize(index => (prop, value) => {
    this.setState(prevState => ({
      selectedConditions: collection.update(
        index,
        flow(
          editableDocument.update(prop, value, OfferDecisionConditionContainer.getItemBusinessRules(this.props)),
          editableDocument.validate(OfferDecisionConditionContainer.getItemValidationRules(), true)
        ),
        prevState.selectedConditions
      )
    }));
  });

  save = async () => {
    const selectedConditions = this.state.selectedConditions.map(
      editableDocument.validate(OfferDecisionConditionContainer.getItemValidationRules(), false)
    );

    if (!selectedConditions.every(x => x.validationInfo.isValid)) {
      this.setState({
        selectedConditions
      });
      return;
    }

    this.setState({
      selectedConditions,
      saving: true
    });

    try {
      const event = await this.props.actions.updateOfferDecisionSelectedConditions(
        this.props.documentId,
        this.props.siteId,
        this.state.selectedConditions.map(x => x.document)
      );
      if (event.ok) {
        this.setState(prevState => ({
          saving: false,
          selectedConditions: prevState.selectedConditions.map(x =>
            editableDocument.create({
              ...x.document,
              isEditable: false
            })
          )
        }));
      } else {
        this.setState({ saving: false });
      }
    } catch (error) {
      this.setState({ saving: false });
    }
  };

  addNewItem = () => {
    this.setState(prevState => ({
      selectedConditions: collection.insert(
        prevState.selectedConditions.length,
        editableDocument.create({
          decisionConditionId: '',
          composedCode: '',
          user: this.props.userProfile.name,
          comment: '',
          isEditable: true
        }),
        prevState.selectedConditions
      )
    }));
  };

  removeItem = memoize(idx => () => {
    this.setState(prevState => ({
      selectedConditions: collection
        .remove(idx, prevState.selectedConditions)
        .map(editableDocument.validate(OfferDecisionConditionContainer.getItemValidationRules(), true))
    }));
  });

  render() {
    const { selectedConditions, saving, loading } = this.state;
    const { availableConditions, t } = this.props;
    const selectedIds = selectedConditions.map(x => x.document.decisionConditionId);
    const disableAddButton = availableConditions.length === selectedConditions.length;

    return (
      <CollapseCard icon={AssignmentLate} iconColor='primary' title={t('OfferEdit.Decision.Condition.Title')} toggleOnHeaderClick>
        {loading ? (
          <WrappedFakeText lines={6} />
        ) : (
          <OfferDecisionConditionListComponent
            selectedConditions={selectedConditions}
            remainingConditions={this.getRemainingConditions(availableConditions, ...selectedIds)}
            disableAddButton={disableAddButton}
            onAdd={this.addNewItem}
            onRemoveItem={this.removeItem}
            onPropertyChange={this.changeItemProperty}
            onSave={this.save}
            saving={saving}
          />
        )}
      </CollapseCard>
    );
  }
}

OfferDecisionConditionContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  selectedConditions: PropTypes.array.isRequired,
  availableConditions: PropTypes.array.isRequired,
  userProfile: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  let userProfile;
  if (state.oidc.user && state.oidc.user.profile) userProfile = state.oidc.user.profile;
  return {
    selectedConditions: state.currentOffer.decision.selectedConditions,
    availableConditions: state.currentOffer.decision.availableConditions,
    userProfile: userProfile
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      loadOfferDecisionSelectedConditions,
      updateOfferDecisionSelectedConditions,
      loadOfferDecisionAvailableConditions
    },
    dispatch
  )
});

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(OfferDecisionConditionContainer);
